import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

const LOGO_URL = "/assets/logo.png";

// Detect platform synchronously before render
const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
const isAndroid = /android/.test(navigator.userAgent.toLowerCase());
// Detect iOS Safari specifically
const isIOSSafari =
  isIOS &&
  /safari/.test(navigator.userAgent.toLowerCase()) &&
  !/crios|fxios|edgios|opios/.test(navigator.userAgent.toLowerCase());
const isIOSNonSafari = isIOS && !isIOSSafari;

const InviteRedirect = () => {
  const [showHelp, setShowHelp] = useState(false);
  const [copied, setCopied] = useState(false);
  const { inviteCode } = useParams();

  // OGP values - use current domain for backward compatibility
  const currentDomain = window.location.hostname;
  const isLocalhost =
    currentDomain === "localhost" || currentDomain === "127.0.0.1";
  const webUrl = `https://${currentDomain}/${inviteCode}`;
  const universalLink = `https://${currentDomain}/invite/${inviteCode}`;
  const safariUniversalLink = `x-safari-https://${currentDomain}/invite/${inviteCode}`;
  const shouldShowOpenWithYou = !isIOS || isIOSSafari;
  const title = "Join me on WithYou!";
  const description =
    "I'm inviting you to join me on WithYou. Tap to get started!";
  const imageUrl = `https://${currentDomain}/assets/logo.png`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(universalLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // ignore
    }
  };

  useEffect(() => {
    if (!inviteCode) return;

    // For Android, use Play Store URL with referrer (skipped on localhost)
    if (isAndroid && !isLocalhost) {
      const encodedInvite = encodeURIComponent(inviteCode);
      window.location.href = `https://play.google.com/store/apps/details?id=com.metastudiosinc.withyou&referrer=invite%3D${encodedInvite}`;
    }
  }, [inviteCode, isLocalhost]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={webUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:site_name" content="WithYou" />
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={webUrl} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={imageUrl} />
        {/* App links */}
        <meta property="al:ios:app_name" content="WithYou" />
        <meta property="al:ios:url" content={universalLink} />
        <meta property="al:android:url" content={universalLink} />
        <meta property="al:android:app_name" content="WithYou" />
        {/* iOS Smart App Banner - using JSX interpolation */}
        <meta
          name="apple-itunes-app"
          content={`app-id=1616729093, app-argument=${universalLink}`}
        />
      </Helmet>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          padding: "10vh 20px 24px",
        }}
      >
        <div style={{ width: "100%", maxWidth: 480, textAlign: "center" }}>
          <a href={universalLink} style={{ display: "inline-block" }}>
            <img
              src={LOGO_URL}
              alt="WithYou Logo"
              style={{
                width: 72,
                height: 72,
                marginBottom: 16,
                cursor: "pointer",
              }}
            />
          </a>
          {shouldShowOpenWithYou && (
            <>
              <div style={{ marginBottom: 8 }}>
                <a
                  href={universalLink}
                  style={{
                    display: "inline-block",
                    padding: "12px 20px",
                    background: "#1a73e8",
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: 8,
                    fontWeight: "bold",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                  }}
                >
                  Open WithYou
                </a>
              </div>
              <div style={{ fontSize: 14, color: "#444", marginBottom: 16 }}>
                Opens your invite in the app.
              </div>
            </>
          )}
        </div>
        {isIOSSafari && (
          <>
            <div
              style={{
                textAlign: "center",
                maxWidth: 420,
                fontSize: 14,
                color: "#444",
              }}
            >
              Install WithYou to continue.
            </div>
            <a
              href="https://apps.apple.com/us/app/withyou/id1616729093"
              target="_blank"
              rel="noopener noreferrer"
              style={{ margin: "10px 8px", display: "inline-block" }}
            >
              <img
                src="/assets/appstore.svg"
                alt="Download on the App Store"
                style={{ width: 260, height: "auto", display: "block" }}
              />
            </a>
          </>
        )}
        {isIOSNonSafari && (
          <>
            <div
              style={{
                textAlign: "center",
                maxWidth: 420,
                fontSize: 14,
                color: "#444",
              }}
            >
              Open in Safari to launch the app, or install WithYou below.
            </div>
            {!isLocalhost && (
              <a
                href={safariUniversalLink}
                style={{
                  display: "inline-block",
                  padding: "12px 20px",
                  background: "#1a73e8",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 8,
                  fontWeight: "bold",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                  marginTop: 8,
                }}
              >
                Open in Safari
              </a>
            )}
            <a
              href="https://apps.apple.com/us/app/withyou/id1616729093"
              target="_blank"
              rel="noopener noreferrer"
              style={{ margin: "10px 8px", display: "inline-block" }}
            >
              <img
                src="/assets/appstore.svg"
                alt="Download on the App Store"
                style={{ width: 260, height: "auto", display: "block" }}
              />
            </a>
            <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
              Try the button above first.
            </div>
          </>
        )}
        {isAndroid && (
          <>
            <div
              style={{
                textAlign: "center",
                maxWidth: 420,
                fontSize: 14,
                color: "#444",
              }}
            >
              If the app doesn’t open, install WithYou from Google Play.
            </div>
            <a
              href="https://play.google.com/store/apps/details?id=com.metastudiosinc.withyou"
              target="_blank"
              rel="noopener noreferrer"
              style={{ margin: "10px 8px", display: "inline-block" }}
            >
              <img
                src="/assets/playstore.svg"
                alt="Get it on Google Play"
                style={{ width: 260, height: "auto", display: "block" }}
              />
            </a>
            <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
              Try the button above first.
            </div>
          </>
        )}
        {!isIOS && !isAndroid && (
          <>
            <p style={{ textAlign: "center", maxWidth: 420 }}>
              Open this link on your phone to continue in WithYou, or install
              the app:
            </p>
            <div
              className="store-row"
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "center",
                alignItems: "center",
                marginTop: 8,
              }}
            >
              <a
                href="https://apps.apple.com/us/app/withyou/id1616729093"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-block" }}
              >
                <img
                  src="/assets/appstore.svg"
                  alt="Download on the App Store"
                  style={{ width: 200, height: "auto" }}
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.metastudiosinc.withyou"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-block" }}
              >
                <img
                  src="/assets/playstore.svg"
                  alt="Get it on Google Play"
                  style={{ width: 200, height: "auto" }}
                />
              </a>
            </div>
          </>
        )}
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <button
            onClick={() => setShowHelp(!showHelp)}
            style={{
              background: "transparent",
              border: 0,
              color: "#1a73e8",
              cursor: "pointer",
            }}
          >
            Not opening?
          </button>
          {showHelp && (
            <div style={{ fontSize: 13, color: "#666", marginTop: 8 }}>
              {isIOS ? (
                <div>Open this page in Safari to launch the app.</div>
              ) : isAndroid ? (
                <div>Make sure the app is installed, then try again.</div>
              ) : (
                <div>Open on your phone to launch the app directly.</div>
              )}
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            {shouldShowOpenWithYou && (
              <>
                <a href={universalLink} style={{ color: "#1a73e8" }}>
                  try again
                </a>
                <span style={{ margin: "0 8px", color: "#bbb" }}>|</span>
              </>
            )}
            {(!isIOS || isIOSSafari) && (
              <button
                onClick={copyLink}
                style={{
                  background: "transparent",
                  border: 0,
                  color: "#1a73e8",
                  cursor: "pointer",
                }}
              >
                {copied ? "copied" : "copy link"}
              </button>
            )}
          </div>
        </div>

        <style>{`
          @media (max-width: 640px) {
            .store-row {
              flex-direction: column;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default InviteRedirect;
